import { capitalize } from 'lodash';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { DeleteOutlined } from '@ant-design/icons';
import { enumStringKeys } from '@sonar-software/sonar-ts-lib';
import { SonarText } from '@ui/text';
import { When } from 'react-if';
import { Button, Form, Input, Select } from 'antd';
import { ClassificationLevel, coreActions } from '@sonar-software/sonar-core';
import React, { useCallback, useMemo } from 'react';
import { useAction, useAsyncState, useOnMount, useSalesforceUsers } from '@hooks';
const operators = {
    action: COPY_SPECIFIC.CONTAINS,
    classification: COPY_SPECIFIC.IS_ANY,
    section: COPY_SPECIFIC.EQUALS,
    user: COPY_SPECIFIC.IS_ANY
};
const sectionsSelectors = createAsyncSelectors((state) => state.core.dataSecurity.setupAuditTrail.sections.get);
export const SetupAuditTrailRule = ({ field, canDelete, onDelete, ruleType }) => {
    const { data: sectionData, isRequested: sectionRequested } = useAsyncState(sectionsSelectors);
    const userData = useSalesforceUsers();
    const form = Form.useFormInstance();
    const selectedCondition = Form.useWatch(['monitorConfig', 'rules', field.name, 'condition']);
    const sectionOptions = useMemo(() => {
        var _a;
        return (_a = sectionData === null || sectionData === void 0 ? void 0 : sectionData.map((section) => ({
            label: section,
            value: section
        }))) !== null && _a !== void 0 ? _a : [];
    }, [sectionData]);
    const classificationOptions = useMemo(() => enumStringKeys(ClassificationLevel).reverse()
        .map((classification) => ({
        label: classification,
        value: classification
    })), []);
    const userOptions = useMemo(() => {
        var _a;
        return (_a = userData === null || userData === void 0 ? void 0 : userData.map((user) => ({
            label: user.username,
            value: user.username
        }))) !== null && _a !== void 0 ? _a : [];
    }, [userData]);
    const getSetupAuditTrailSections = useAction(coreActions.requestGetCoreSetupAuditTrailSections);
    useOnMount(() => {
        getSetupAuditTrailSections();
    });
    const getConditionOptions = useCallback(() => {
        const currentRuleConditions = form.getFieldValue(['monitorConfig', 'rules']).reduce((previous, current, index) => {
            if (current && index !== field.name) {
                return [...previous, current.condition];
            }
            return [...previous];
        }, []);
        let conditionOptions = [
            {
                label: COPY_SPECIFIC.ACTION,
                value: 'action'
            }
        ];
        if (!(currentRuleConditions === null || currentRuleConditions === void 0 ? void 0 : currentRuleConditions.includes('section'))) {
            conditionOptions = [{
                    label: COPY_SPECIFIC.SECTION,
                    value: 'section'
                }, ...conditionOptions];
        }
        if (!(currentRuleConditions === null || currentRuleConditions === void 0 ? void 0 : currentRuleConditions.includes('classification'))) {
            conditionOptions = [{
                    label: COPY_SPECIFIC.CLASSIFICATION,
                    value: 'classification'
                }, ...conditionOptions];
        }
        if (!(currentRuleConditions === null || currentRuleConditions === void 0 ? void 0 : currentRuleConditions.includes('user'))) {
            conditionOptions = [
                ...conditionOptions,
                {
                    label: COPY_SPECIFIC.USER,
                    value: 'user'
                }
            ];
        }
        return conditionOptions;
    }, [form, field]);
    const initialCondition = useMemo(() => {
        const currentRuleConditions = form.getFieldValue(['monitorConfig', 'rules']).reduce((previous, current) => {
            if (current) {
                return [...previous, current.condition];
            }
            return [...previous];
        }, []);
        if (!(currentRuleConditions === null || currentRuleConditions === void 0 ? void 0 : currentRuleConditions.includes('classification'))) {
            return 'classification';
        }
        if (!(currentRuleConditions === null || currentRuleConditions === void 0 ? void 0 : currentRuleConditions.includes('section'))) {
            return 'section';
        }
        if (!(currentRuleConditions === null || currentRuleConditions === void 0 ? void 0 : currentRuleConditions.includes('user'))) {
            return 'user';
        }
        return 'action';
    }, [form]);
    const getDefaultValue = useCallback((newCondition) => {
        if (newCondition === 'classification') {
            return [ClassificationLevel.Critical];
        }
        if (newCondition === 'section') {
            return sectionOptions.length > 0 ? [sectionOptions[0].value] : undefined;
        }
        return [];
    }, [sectionOptions]);
    const onConditionChange = useCallback((condition) => {
        form.setFieldValue(['monitorConfig', 'rules', field.name, 'value'], getDefaultValue(condition));
    }, [field, form, getDefaultValue]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", null,
            React.createElement(When, { condition: field.name !== 0 },
                React.createElement(SonarText, null, capitalize(ruleType)))),
        React.createElement(Form.Item, { initialValue: initialCondition, name: [field.name, 'condition'] },
            React.createElement(Select, { onChange: onConditionChange, options: getConditionOptions() })),
        React.createElement("div", null,
            React.createElement(SonarText, null, selectedCondition ? operators[selectedCondition] : '')),
        React.createElement(When, { condition: selectedCondition === 'action' },
            React.createElement(Form.Item, { name: [field.name, 'value', 0] },
                React.createElement(Input, null))),
        React.createElement(When, { condition: selectedCondition === 'classification' },
            React.createElement(Form.Item, { initialValue: getDefaultValue(initialCondition), name: [field.name, 'value'] },
                React.createElement(Select, { mode: 'multiple', options: classificationOptions }))),
        React.createElement(When, { condition: selectedCondition === 'section' },
            React.createElement(Form.Item, { initialValue: getDefaultValue(initialCondition), name: [field.name, 'value'] },
                React.createElement(Select, { loading: sectionRequested, mode: 'multiple', options: sectionOptions, placeholder: COPY_SPECIFIC.NO_SECTIONS_AVAILABLE }))),
        React.createElement(When, { condition: selectedCondition === 'user' },
            React.createElement(Form.Item, { initialValue: getDefaultValue(initialCondition), name: [field.name, 'value'], required: true },
                React.createElement(Select, { mode: 'multiple', options: userOptions, placeholder: COPY_SPECIFIC.SELECT_USER }))),
        React.createElement("div", null,
            React.createElement(When, { condition: canDelete },
                React.createElement(Button, { className: 'delete-rule-button', icon: React.createElement(DeleteOutlined, null), onClick: onDelete, style: { alignSelf: 'flex-start' } })))));
};
